import { supabase } from '../supabaseClient';
import {
  parseSubscriptionPassStateRecord,
  resolveAccountPassCategory,
  type AccountPassCategory,
  ACCOUNT_PASS_CATEGORY_LABEL,
  ACCOUNT_PASS_CATEGORY_PARTS,
} from '../utils/subscriptionPassStorage';

export type { AccountPassCategory };
export { ACCOUNT_PASS_CATEGORY_LABEL, ACCOUNT_PASS_CATEGORY_PARTS };

// ─────────────────────────────────────────
// Types
// ─────────────────────────────────────────

export interface PlatformMessage {
  id: string;
  sender_user_id: string | null;
  title: string;
  body: string;
  recipient_type: 'all' | 'individual';
  is_recalled: boolean;
  created_at: string;
  updated_at: string;
  /** 관리자 조회 시 읽은 이용자 수 (선택적) */
  read_count?: number;
  /** 수신자 목록 (개별 발송 시) */
  recipients?: { user_id: string; email?: string; display_name?: string }[];
}

export interface PlatformMessageSendPayload {
  /** 제목 — UI에서 사용하지 않아 빈 문자열로 발송 */
  title?: string;
  body: string;
  recipientType: 'all' | 'individual';
  /** recipientType='individual' 일 때 대상 user_id 목록 */
  recipientUserIds?: string[];
}

// ─────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────

function mapMessage(row: Record<string, unknown>): PlatformMessage {
  return {
    id: String(row.id),
    sender_user_id: row.sender_user_id != null ? String(row.sender_user_id) : null,
    title: String(row.title ?? ''),
    body: String(row.body ?? ''),
    recipient_type: (row.recipient_type === 'individual' ? 'individual' : 'all') as 'all' | 'individual',
    is_recalled: Boolean(row.is_recalled),
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
  };
}

// ─────────────────────────────────────────
// 이용자용 함수
// ─────────────────────────────────────────

/**
 * 현재 로그인 이용자에게 발송된 메시지 목록 (회수되지 않은 것).
 * recipient_type='all' OR 개별 지정된 경우 모두 포함.
 */
export async function listMyMessages(): Promise<PlatformMessage[]> {
  const { data, error } = await supabase
    .from('platform_messages')
    .select('id, sender_user_id, title, body, recipient_type, is_recalled, created_at, updated_at')
    .eq('is_recalled', false)
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => mapMessage(r as Record<string, unknown>));
}

/**
 * 현재 로그인 이용자의 읽지 않은 메시지 수.
 * 빠른 badge 용도 — 메시지 목록을 가져온 뒤 reads 집합과 비교한다.
 */
export async function countMyUnreadMessages(): Promise<number> {
  // 1. 수신 가능한 메시지 id 목록
  const { data: msgData, error: msgError } = await supabase
    .from('platform_messages')
    .select('id')
    .eq('is_recalled', false);

  if (msgError) throw new Error(msgError.message);
  const msgIds = (msgData ?? []).map((r) => String((r as Record<string, unknown>).id));
  if (!msgIds.length) return 0;

  // 2. 이미 읽은 message_id 집합
  const { data: readData, error: readError } = await supabase
    .from('platform_message_reads')
    .select('message_id')
    .in('message_id', msgIds);

  if (readError) throw new Error(readError.message);
  const readIds = new Set((readData ?? []).map((r) => String((r as Record<string, unknown>).message_id)));

  return msgIds.filter((id) => !readIds.has(id)).length;
}

/**
 * 메시지를 읽음 처리한다 (upsert).
 */
export async function markMessageRead(messageId: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const { error } = await supabase
    .from('platform_message_reads')
    .upsert(
      { message_id: messageId, user_id: user.id, read_at: new Date().toISOString() },
      { onConflict: 'message_id,user_id' }
    );

  if (error) throw new Error(error.message);
}

/**
 * 여러 메시지를 한 번에 읽음 처리한다.
 */
export async function markAllMessagesRead(messageIds: string[]): Promise<void> {
  if (!messageIds.length) return;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const now = new Date().toISOString();
  const rows = messageIds.map((mid) => ({
    message_id: mid,
    user_id: user.id,
    read_at: now,
  }));

  const { error } = await supabase
    .from('platform_message_reads')
    .upsert(rows, { onConflict: 'message_id,user_id' });

  if (error) throw new Error(error.message);
}

/**
 * 현재 이용자가 읽은 message_id 집합을 반환한다.
 */
export async function getMyReadMessageIds(messageIds: string[]): Promise<Set<string>> {
  if (!messageIds.length) return new Set();

  const { data, error } = await supabase
    .from('platform_message_reads')
    .select('message_id')
    .in('message_id', messageIds);

  if (error) throw new Error(error.message);
  return new Set((data ?? []).map((r) => String((r as Record<string, unknown>).message_id)));
}

// ─────────────────────────────────────────
// 관리자용 함수
// ─────────────────────────────────────────

/**
 * 관리자용: 전체 발송 메시지 목록 (회수된 것 포함).
 */
export async function listAdminMessages(): Promise<PlatformMessage[]> {
  const { data, error } = await supabase
    .from('platform_messages')
    .select('id, sender_user_id, title, body, recipient_type, is_recalled, created_at, updated_at')
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => mapMessage(r as Record<string, unknown>));
}

/**
 * 관리자용: 특정 메시지의 수신자 목록 조회 (개별 발송 시).
 */
export async function getMessageRecipients(
  messageId: string
): Promise<{ user_id: string }[]> {
  const { data, error } = await supabase
    .from('platform_message_recipients')
    .select('user_id')
    .eq('message_id', messageId);

  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => ({ user_id: String((r as Record<string, unknown>).user_id) }));
}

/**
 * 관리자용: 메시지 발송.
 */
export async function sendMessage(payload: PlatformMessageSendPayload): Promise<PlatformMessage> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('로그인이 필요합니다.');

  const { data, error } = await supabase
    .from('platform_messages')
    .insert({
      sender_user_id: user.id,
      title: (payload.title ?? '').trim(),
      body: payload.body.trim(),
      recipient_type: payload.recipientType,
    })
    .select('id, sender_user_id, title, body, recipient_type, is_recalled, created_at, updated_at')
    .single();

  if (error) throw new Error(error.message);
  const message = mapMessage(data as Record<string, unknown>);

  // 개별 발송 시 수신자 등록
  if (payload.recipientType === 'individual' && payload.recipientUserIds?.length) {
    const recipientRows = payload.recipientUserIds.map((uid) => ({
      message_id: message.id,
      user_id: uid,
    }));
    const { error: recErr } = await supabase
      .from('platform_message_recipients')
      .insert(recipientRows);
    if (recErr) throw new Error(recErr.message);
  }

  return message;
}

/**
 * 관리자용: 메시지 본문·수신자 수정.
 */
export async function updateMessage(
  messageId: string,
  payload: { body: string; recipientUserIds: string[] }
): Promise<PlatformMessage> {
  const body = payload.body.trim();
  if (!body) throw new Error('메시지 내용을 입력하세요.');
  if (!payload.recipientUserIds.length) {
    throw new Error('발송 대상 이용자를 1명 이상 선택하세요.');
  }

  const { data, error } = await supabase
    .from('platform_messages')
    .update({
      body,
      title: '',
      recipient_type: 'individual',
      updated_at: new Date().toISOString(),
    })
    .eq('id', messageId)
    .select('id, sender_user_id, title, body, recipient_type, is_recalled, created_at, updated_at')
    .single();

  if (error) throw new Error(error.message);
  const message = mapMessage(data as Record<string, unknown>);

  // 수신자 교체
  const { error: delErr } = await supabase
    .from('platform_message_recipients')
    .delete()
    .eq('message_id', messageId);
  if (delErr) throw new Error(delErr.message);

  const recipientRows = payload.recipientUserIds.map((uid) => ({
    message_id: messageId,
    user_id: uid,
  }));
  const { error: insErr } = await supabase
    .from('platform_message_recipients')
    .insert(recipientRows);
  if (insErr) throw new Error(insErr.message);

  return message;
}

/**
 * 관리자용: 메시지 회수 (is_recalled=true).
 */
export async function recallMessage(messageId: string): Promise<void> {
  const { error } = await supabase
    .from('platform_messages')
    .update({ is_recalled: true, updated_at: new Date().toISOString() })
    .eq('id', messageId);

  if (error) throw new Error(error.message);
}

/**
 * 관리자용: 메시지 영구 삭제.
 * RLS에 delete 정책이 없으면 에러 없이 0행 삭제될 수 있어 select로 확인한다.
 */
export async function deleteMessage(messageId: string): Promise<void> {
  const { data, error } = await supabase
    .from('platform_messages')
    .delete()
    .eq('id', messageId)
    .select('id');

  if (error) throw new Error(error.message);
  if (!data?.length) {
    throw new Error('메시지를 삭제하지 못했습니다. 관리자 권한(DELETE RLS)을 확인하세요.');
  }
}

export interface PlatformUserInfo {
  user_id: string;
  email: string;
  display_name: string;
  pass_category: AccountPassCategory;
}

/**
 * 관리자용: 플랫폼 전체 이용자 목록 (개별 발송 대상 선택용).
 * admin_list_all_platform_users RPC — auth.users 전체 반환.
 * 이용권 정보(pass_category)를 platform_user_subscription_passes 와 병합해 반환한다.
 */
export async function listPlatformUsers(_workspaceId?: string): Promise<PlatformUserInfo[]> {
  // 1. 전체 이용자 목록
  const { data: usersData, error: usersError } = await supabase.rpc('admin_list_all_platform_users');
  if (usersError) throw new Error(usersError.message);

  // 2. 이용권 정보 (오류 시 무시하고 'free' 처리)
  const passCategoryById = new Map<string, AccountPassCategory>();
  try {
    const { data: passData } = await supabase
      .from('platform_user_subscription_passes')
      .select('user_id, individual_history, group_history, event_history, event_pass_period, session_group_pass_binding');
    for (const raw of passData ?? []) {
      const row = raw as Record<string, unknown>;
      const uid = String(row.user_id ?? '');
      if (!uid) continue;
      const state = parseSubscriptionPassStateRecord({
        individualHistory: row.individual_history,
        groupHistory: row.group_history,
        eventHistory: row.event_history,
        eventPassPeriod: row.event_pass_period,
        sessionGroupPassBinding: row.session_group_pass_binding,
      });
      passCategoryById.set(uid, resolveAccountPassCategory(state));
    }
  } catch {
    // pass 정보 없으면 'free' 로 대체
  }

  return (usersData ?? []).map((r: unknown) => {
    const row = r as Record<string, unknown>;
    const uid = String(row.user_id ?? '');
    return {
      user_id: uid,
      email: String(row.email ?? ''),
      display_name: String(row.display_name ?? ''),
      pass_category: passCategoryById.get(uid) ?? 'free',
    };
  });
}
